import DateHelper from '../../Utility/DateHelper';
import { Random } from '../../Utility/Random';
import { track, TrackMethod } from '../../Utility/Tracking/Track';
import {
    IFriendData,
    IPlayerState,
    IPlayerStateTaskData,
} from './IPlayerState';
import {
    setBoosterJoker,
    setBoosterTNT,
    setCoins,
    setLives,
} from './PlayerInventoryHelper';
import { ProductID, ProductValueMap } from './Product';
import { getDefaultBoosterPrices } from '../Level/LevelStateHelper';
import { trackStatePurchase } from '../../Utility/Tracking/TrackingHelpers/Transaction';
import {
    TActionType,
    TransactionType,
} from '../../Utility/Tracking/DataSet/Transaction';
import { IGameState } from '../Game/IGameState';

export function setStateTaskData(
    state: IPlayerState,
    taskData: IPlayerStateTaskData
): IPlayerState {
    return {
        ...state,
        tasks: [...state.tasks, taskData],
    };
}

export function subscribeBotOptIn(state: IPlayerState): IPlayerState {
    // Player got 500 coins in the first subscribe.
    if (state.optInReward === 0) {
        state = setCoins(state, state.coins + 500);
        return {
            ...state,
            optInReward: 1,
        };
    }

    return state;
}

export function addShortcut(state: IPlayerState): IPlayerState {
    if (state.shortcutReward === 0) {
        state = setCoins(state, state.coins + 500);
        return {
            ...state,
            shortcutReward: 1,
        };
    }

    return state;
}

export function unlockNextWorld(state: IPlayerState): IPlayerState {
    return {
        ...state,
        world: state.world + 1, // Increment player's world number.
        gateTimer: null, // Gate timer is reset.
        gateFriends: [], // Gate friends list is not carried forward for the next gate.
    };
}

/**
 * Get the total number of stars the player has earned in the specified world.
 * @param state
 * @param world
 */
export function getWorldStarsTotal(state: IPlayerState, world: number): number {
    return state.levels.saga.reduce(
        (a, b) => (Math.ceil(b.levelNumber / 60) === world ? a + b.stars : a),
        0
    );
}

/**
 * Add a friend ID (AND friend's avatar) to the gate-friends list.
 * The avatar path is added for backwards compatibility.
 * @param state
 * @param friendID
 * @param avatarPath
 */
export function addGateFriend(
    state: IPlayerState,
    friendID: string,
    avatarPath: string
): IPlayerState {
    // Validate this friend does not already exist and there is not already 3 friends.
    if (
        state.gateFriends.indexOf(friendID) !== -1 ||
        state.gateFriends.length >= 6
    ) {
        return state;
    }

    return {
        ...state,
        gateFriends: [...state.gateFriends, friendID, avatarPath],
    };
}

export function clearTasks(state: IPlayerState): IPlayerState {
    return {
        ...state,
        tasks: [],
    };
}

export function consumePurchase(
    state: IGameState,
    productID: ProductID
): IGameState {
    const product = ProductValueMap[productID];
    if (!product) return state;
    const productPrices = getDefaultBoosterPrices();

    if (product.lives !== undefined) {
        track('tLivesReceived', product.lives, TrackMethod.ADD);
        state = {
            ...state,
            playerState: setLives(
                state.playerState,
                state.playerState.lives + product.lives
            ),
        };
    }
    if (product.boosterTNT !== undefined) {
        track(
            'mBoostersBoughtWithRealMoney',
            product.boosterTNT,
            TrackMethod.ADD
        );
        track('tMassRemoveReceived', product.boosterTNT, TrackMethod.ADD);
        track('tTransactionBoosters', product.boosterTNT, TrackMethod.ADD);
        track(
            'tTransactionCoinWorth',
            product.boosterTNT * productPrices.tnt,
            TrackMethod.ADD
        );
        state = {
            ...state,
            playerState: setBoosterTNT(
                state.playerState,
                state.playerState.inventory.boosterTNT + product.boosterTNT
            ),
        };
    }
    if (product.boosterJokers !== undefined) {
        track(
            'mBoostersBoughtWithRealMoney',
            product.boosterJokers,
            TrackMethod.ADD
        );
        track('tJokerReceived', product.boosterJokers, TrackMethod.ADD);
        track('tTransactionBoosters', product.boosterJokers, TrackMethod.ADD);
        track(
            'tTransactionCoinWorth',
            product.boosterJokers * productPrices.joker,
            TrackMethod.ADD
        );
        state = {
            ...state,
            playerState: setBoosterJoker(
                state.playerState,
                state.playerState.inventory.boosterJoker + product.boosterJokers
            ),
        };
    }

    if (product.coins !== undefined) {
        track('mCoinsBoughtWithRealMoney', product.coins, TrackMethod.ADD);
        track('tTransactionCoins', product.coins, TrackMethod.ADD);
        track('tTransactionCoinWorth', product.coins, TrackMethod.ADD);
        state = {
            ...state,
            playerState: setCoins(
                state.playerState,
                state.playerState.coins + product.coins
            ),
        };
    }

    trackStatePurchase(
        state,
        TransactionType.PURCHASE,
        TActionType.INVENTORY_PURCHASE
    );

    return {
        ...state,
        playerState: {
            ...state.playerState,
            purchases: state.playerState.purchases + 1,
        },
    };
}

export function sendLife(
    playerState: IPlayerState,
    friendId: string
): IPlayerState {
    // Validate can send life to friend.
    let friendData;
    ({ playerState, friendData } = getFriendData(playerState, friendId));
    if (friendData.lifeSendTimer !== null) return playerState;

    // Increment life-share-count.
    playerState = setLifeShareCount(
        playerState,
        playerState.lifeShareCount + 1
    );

    // Update friend data.
    return {
        ...playerState,
        friendData: playerState.friendData.map(friend => {
            if (friend.playerId !== friendId) {
                return friend;
            }

            return {
                ...friend,
                lifeSendTimer: DateHelper.now().toString(),
            };
        }),
    };
}

export function requestLife(
    playerState: IPlayerState,
    friendId: string
): IPlayerState {
    // Validate can request life from friend.
    let friendData;
    ({ playerState, friendData } = getFriendData(playerState, friendId));
    if (friendData.lifeRequestTimer !== null) return playerState;

    // Increment life-share-count.
    playerState = setLifeShareCount(
        playerState,
        playerState.lifeShareCount + 1
    );

    // Update friend data.
    return {
        ...playerState,
        friendData: playerState.friendData.map(friend => {
            if (friend.playerId !== friendId) {
                return friend;
            }

            return {
                ...friend,
                lifeRequestTimer: DateHelper.now().toString(),
            };
        }),
    };
}

/**
 * Initialize friend data if it does not exists.
 * @param playerState
 * @param friendId
 */
export function getFriendData(
    playerState: IPlayerState,
    friendId: string
): { playerState: IPlayerState; friendData: IFriendData } {
    let friendData: IFriendData = playerState.friendData.find(
        friend => friend.playerId === friendId
    );
    // Find friend data. Initialize if not yet stored.
    if (!friendData) {
        friendData = {
            playerId: friendId,
            lifeRequestTimer: null,
            lifeSendTimer: null,
            giftTimer: null,
            helperTimer: null,
        };
        // Add new friend.
        playerState = {
            ...playerState,
            friendData: [...playerState.friendData, friendData],
        };
    }
    if (!friendData.helperTimer) {
        friendData = {
            playerId: friendId,
            lifeRequestTimer: friendData.lifeRequestTimer,
            lifeSendTimer: friendData.lifeSendTimer,
            giftTimer: friendData.giftTimer,
            helperTimer: null,
        };
    }

    return { playerState: playerState, friendData: friendData };
}

export function setLifeShareCount(playerState: IPlayerState, count: number) {
    if (count >= 10) {
        count = 0;

        // 10 or more life-shares -> give prize (150 coins).
        playerState = {
            ...setCoins(playerState, playerState.coins + 150, true),

            // Set life-share-timer
            lifeSharePrizeTimer: DateHelper.now().toString(),
        };
    }

    return {
        ...playerState,
        lifeShareCount: count,
    };
}

/**
 * Handle a successful 'friend challenge' / 'send gift to friend'.
 * Player earns 15-25 coins and a cooldown is applied for specified friend.
 * @param playerState
 * @param friendId
 */
export function sendGift(
    playerState: IPlayerState,
    friendId: string
): IPlayerState {
    // Validate can send gift to friend.
    let friendData;
    ({ playerState, friendData } = getFriendData(playerState, friendId));
    if (friendData.giftTimer !== null) return playerState;
    playerState = setCoins(
        playerState,
        playerState.coins + Random.int(15, 25),
        true
    );
    // Update friend data.
    return {
        ...playerState,
        friendData: playerState.friendData.map(friend => {
            if (friend.playerId !== friendId) {
                return friend;
            }

            return {
                ...friend,
                giftTimer: DateHelper.now().toString(),
            };
        }),
    };
}
